import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  applyIfLatest,
  latestRequestInitialState,
  trackLatestRequests,
} from 'state/latestRequest';

import { LsKeys } from 'enums';
import {
  decideProviderPhoto,
  decideProviderVerification,
  getDoctor,
  getDoctorFinances,
  getDoctors,
  getDoctorVisits,
  getPendingPhotos,
  getPendingPhotosCount,
  getPendingProviders,
  getPendingProvidersCount,
} from 'state/thunks/doctorThunk/doctorThunk';
import { editFinance } from 'state/thunks/financeThunk/financeThunk';
import {
  createNotification,
  getNotifications,
} from 'state/thunks/notificationThunk/notificationThunk';
import {
  editUser,
  getPatientVisits,
  getUser,
  getUsers,
} from 'state/thunks/userThunk/userThunk';

const initialState = {
  doctors: null,
  doctor: null,
  count: null,
  lastPage: null,
  page: null,
  user: null,
  isLoading: false,
  visits: [],
  finances: [],
  /**
   * How many providers are waiting for approval. Drives the sidebar badge and
   * the queue strip above the providers table.
   *
   * Starts at 0 rather than null so the badge is simply absent until the first
   * fetch answers. A badge that flashes an empty pill on every page load reads
   * as a rendering fault rather than as "nothing is waiting".
   */
  pendingCount: 0,
  pendingProviders: null,
  isDeciding: false,
  /**
   * The photo review queue, kept separate from the approval one above.
   *
   * They count different things and are worked at different times — an
   * established provider changing their photo is not waiting for approval — so
   * merging them would make both numbers mean "something about providers",
   * which is not actionable.
   */
  pendingPhotoCount: 0,
  pendingPhotos: null,
  isDecidingPhoto: false,
  /**
   * Why an approval or photo decision did not go through.
   *
   * Both `.rejected` cases used to clear the in-flight flag and drop the
   * payload, and neither card read an error, so a refused decision was
   * indistinguishable from one nobody had clicked: the spinner stopped, the
   * card stayed exactly as it was, and the operator's only reasonable reading
   * was that the button did nothing. The refusals are real -- another
   * administrator deciding first answers PROVIDER_ALREADY_REVIEWED, and a
   * provider with nothing awaiting review answers NO_PHOTO_AWAITING_REVIEW --
   * and both are things the operator needs told rather than left to guess.
   *
   * One slot, shared by the two cards, and they CAN both be on screen: a
   * provider who registered with a photo is `Not verified` with an
   * `avatarPending`, so the approval card and the photo card render together.
   * They cannot be in flight at once -- each card disables its own buttons
   * while deciding, and an operator has one pointer -- so the message is never
   * ambiguous about which request failed. It is ambiguous about which CARD it
   * belongs under, which is why each card renders it inside itself rather than
   * the page rendering it once between them: whichever card the operator just
   * clicked is the one they are looking at.
   */
  decisionError: '' as string,
  ...latestRequestInitialState,
};

/**
 * The filtered list read is guarded by `applyIfLatest`: a search string, a
 * status filter and a page all change it, so overlapping requests are routine
 * and the reducer must apply the newest REQUEST rather than the newest
 * response. See src/state/latestRequest.ts.
 */
const slice = createSlice({
  name: 'doctor',
  initialState,
  reducers: {
    setLogOut: () => initialState,
    /**
     * A refusal outlives the page it was shown on.
     *
     * Two administrators working the approval queue is the normal case, so
     * "already reviewed" is the commonest thing in this slot -- and without a
     * clear it followed the operator to the NEXT provider, telling them a
     * genuinely pending application had already been decided. Same clear, and
     * the same reasoning, as `clearStatusError` on the user slice.
     */
    clearDecisionError: state => {
      state.decisionError = '';
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<any>) => {
    builder
      .addCase(getDoctorVisits.fulfilled, (state, action: PayloadAction<any>) => {
        console.log('get doctor success');
        console.log(action.payload);
        console.log(action.payload.rows);
        state.visits = action.payload;
      })
      .addCase(getDoctorFinances.fulfilled, (state, action: PayloadAction<any>) => {
        console.log('get doctor finances success');
        console.log(action.payload);
        console.log(action.payload.rows);
        state.finances = action.payload;
      })
      .addCase(getDoctor.fulfilled, (state, action: PayloadAction<any>) => {
        console.log('get user success');
        console.log(action.payload);
        console.log(action.payload.rows);
        state.doctor = action.payload;
      })
      .addCase(
        getDoctors.fulfilled,
        applyIfLatest(getDoctors, (state, payload: any) => {
        console.log('get doctor success');
        console.log(payload);
        console.log(payload.rows);
        state.doctors = payload;
        state.count = payload.count;
        state.lastPage = payload.lastPage;
        state.page = payload.page;
        }),
      )
      .addCase(
        getPendingProvidersCount.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.pendingCount = action.payload ?? 0;
        },
      )
      .addCase(
        getPendingProviders.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.pendingProviders = action.payload;
          // The list and the badge must never disagree; both come from the
          // same server-side predicate, so take the count from whichever
          // answered most recently.
          state.pendingCount = action.payload?.count ?? state.pendingCount;
        },
      )
      .addCase(decideProviderVerification.pending, state => {
        state.isDeciding = true;
        state.decisionError = '';
      })
      .addCase(
        decideProviderVerification.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isDeciding = false;
          // Refresh the open profile in place so the review card gives way to
          // the normal status control without needing a reload.
          state.doctor = action.payload;
        },
      )
      .addCase(
        decideProviderVerification.rejected,
        (state, action: PayloadAction<any>) => {
          state.isDeciding = false;
          state.decisionError =
            action.payload || 'Could not record that decision. Please try again.';
        },
      )
      .addCase(getPendingPhotosCount.fulfilled, (state, action: PayloadAction<any>) => {
        state.pendingPhotoCount = action.payload ?? 0;
      })
      .addCase(getPendingPhotos.fulfilled, (state, action: PayloadAction<any>) => {
        state.pendingPhotos = action.payload;
        // Same reasoning as the approval queue: the list and the badge come
        // from one server-side predicate and must never disagree.
        state.pendingPhotoCount = action.payload?.count ?? state.pendingPhotoCount;
      })
      .addCase(decideProviderPhoto.pending, state => {
        state.isDecidingPhoto = true;
        state.decisionError = '';
      })
      .addCase(decideProviderPhoto.fulfilled, (state, action: PayloadAction<any>) => {
        state.isDecidingPhoto = false;
        // Refresh the open profile in place, so the review card gives way to
        // the decided photo without needing a reload. Guarded on the id: the
        // same action is dispatched from the queue on the providers list, where
        // the decided provider is usually NOT the one loaded here, and blindly
        // assigning would swap the open profile for somebody else's.
        if (state.doctor?.id === action.payload?.id) {
          state.doctor = action.payload;
        }
        // Drop the decided row from the queue immediately. The count is
        // re-fetched from the server by the thunk; this is only so the card
        // disappears the moment it is acted on rather than a round trip later.
        if (state.pendingPhotos?.rows) {
          state.pendingPhotos = {
            ...state.pendingPhotos,
            rows: state.pendingPhotos.rows.filter(
              (row: any) => row.id !== action.payload?.id,
            ),
          };
        }
      })
      .addCase(decideProviderPhoto.rejected, (state, action: PayloadAction<any>) => {
        state.isDecidingPhoto = false;
        state.decisionError =
          action.payload || 'Could not record that decision. Please try again.';
      })
      .addCase(editUser.pending, (state, action: PayloadAction<any>) => {
        state.isLoading = true;
      })
      .addCase(editUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
      })
      .addCase(editUser.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
      })
      .addCase(getUsers.rejected, (state, action: PayloadAction<any>) => {
        //state.error = action.payload;
        //state.isLoading = false;
      });

    // Matchers must come after every addCase.
    trackLatestRequests(builder, [getDoctors]);
  },
});

export const doctorActions = slice.actions;

export const doctorReducer = slice.reducer;
